import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Search, FileText, CheckSquare, Loader2, X } from 'lucide-react';

const GlobalSearch = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState({ cases: [], todos: [] });
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const resultsRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      const debounceTimer = setTimeout(() => {
        performSearch();
      }, 300);

      return () => clearTimeout(debounceTimer);
    } else {
      setResults({ cases: [], todos: [] });
      setShowResults(false);
    }
  }, [searchQuery]);

  const performSearch = async () => {
    if (!searchQuery.trim()) {
      setResults({ cases: [], todos: [] });
      setShowResults(false);
      return;
    }

    setLoading(true);
    setShowResults(true);

    try {
      const [casesResponse, todosResponse] = await Promise.all([
        api.get('/cases', {
          params: {
            search: searchQuery,
            limit: 5,
          },
        }),
        api.get('/todos', {
          params: {
            search: searchQuery,
            limit: 5,
          },
        }),
      ]);

      setResults({
        cases: casesResponse.data.data || [],
        todos: todosResponse.data.data || [],
      });
    } catch (error) {
      console.error('Error performing search:', error);
      setResults({ cases: [], todos: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, id) => {
    if (type === 'case') {
      navigate(`/cases/${id}`);
    } else if (type === 'todo') {
      navigate(`/todos/${id}`);
    }
    setShowResults(false);
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (!showResults || results.cases.length + results.todos.length === 0) {
      return;
    }

    const totalResults = results.cases.length + results.todos.length;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < totalResults - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      let currentIndex = 0;
      // Find the item at selectedIndex
      for (const caseItem of results.cases) {
        if (currentIndex === selectedIndex) {
          handleResultClick('case', caseItem._id || caseItem.id);
          return;
        }
        currentIndex++;
      }
      for (const todoItem of results.todos) {
        if (currentIndex === selectedIndex) {
          handleResultClick('todo', todoItem._id || todoItem.id);
          return;
        }
        currentIndex++;
      }
    } else if (e.key === 'Escape') {
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const getResultItem = (index) => {
    let currentIndex = 0;
    for (const caseItem of results.cases) {
      if (currentIndex === index) {
        return { type: 'case', item: caseItem };
      }
      currentIndex++;
    }
    for (const todoItem of results.todos) {
      if (currentIndex === index) {
        return { type: 'todo', item: todoItem };
      }
      currentIndex++;
    }
    return null;
  };

  const totalResults = results.cases.length + results.todos.length;
  const hasResults = totalResults > 0;

  return (
    <div className="relative flex-1 max-w-2xl mx-4" ref={searchRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => {
            if (searchQuery.trim().length >= 2) {
              setShowResults(true);
            }
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search cases or tasks..."
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        />
        {searchQuery && (
          <button
            onClick={() => {
              setSearchQuery('');
              setShowResults(false);
              setResults({ cases: [], todos: [] });
            }}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}
      </div>

      {showResults && (
        <div
          ref={resultsRef}
          className="absolute z-50 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200 max-h-96 overflow-y-auto"
        >
          {loading ? (
            <div className="p-4 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400 mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Searching...</p>
            </div>
          ) : hasResults ? (
            <div>
              {results.cases.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-600" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Cases ({results.cases.length})
                      </span>
                    </div>
                  </div>
                  {results.cases.map((caseItem, index) => {
                    const resultIndex = index;
                    return (
                      <button
                        key={caseItem._id || caseItem.id}
                        onClick={() => handleResultClick('case', caseItem._id || caseItem.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          selectedIndex === resultIndex ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <FileText className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {caseItem.caseNumber || 'Case'}
                            </p>
                            <p className="text-sm text-gray-600 truncate">{caseItem.title}</p>
                            {caseItem.description && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                {caseItem.description}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {results.todos.length > 0 && (
                <div>
                  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <CheckSquare className="h-4 w-4 text-gray-600" />
                      <span className="text-xs font-semibold text-gray-700 uppercase">
                        Tasks ({results.todos.length})
                      </span>
                    </div>
                  </div>
                  {results.todos.map((todoItem, index) => {
                    const resultIndex = results.cases.length + index;
                    return (
                      <button
                        key={todoItem._id || todoItem.id}
                        onClick={() => handleResultClick('todo', todoItem._id || todoItem.id)}
                        className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                          selectedIndex === resultIndex ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <CheckSquare className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {todoItem.title}
                            </p>
                            {todoItem.description && (
                              <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                                {todoItem.description}
                              </p>
                            )}
                            {todoItem.caseId && (
                              <p className="text-xs text-gray-400 mt-1">
                                Case: {todoItem.caseId.caseNumber || 'N/A'}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ) : searchQuery.trim().length >= 2 ? (
            <div className="p-4 text-center">
              <p className="text-sm text-gray-500">No results found</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
